// src/hooks/useTimeBoxCreate.ts - TimeBox Local-first 創建 Hook
import { useState, useCallback, useEffect } from 'react';
import { TimeBox, CreateTimeBoxRequest } from '../../../types/timeBox';
import { createTimeBox } from '../../../services/api/timeBoxes';
import {
  createTimeBoxLocal,
  replaceTimeBoxTempId
} from '../../../services/indexedDB';
import {
  addToSyncQueue
} from '../../../services/syncQueueService';
import { logInfo, logWarn, logError } from '../../../utils/logger';

/**
 * 輕量級 TimeBox 創建 hook
 * 專為 QuickTimeBoxModal 設計，只包含創建功能
 * 參考 useCaptureCreate.ts 的實作模式
 */
export const useTimeBoxCreate = (userId: string) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // 監聽網路狀態變化
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * 創建 TimeBox（離線優先）
   * 與 useCaptureCreate 中的實現保持一致
   */
  const handleCreateTimeBox = useCallback(
    async (timeBoxData: Omit<CreateTimeBoxRequest, 'userId'>): Promise<TimeBox> => {
      setIsSubmitting(true);

      // 生成臨時 ID
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      const now = new Date();

      try {
        // 創建本地 TimeBox 對象
        const localTimeBox: TimeBox = {
          id: tempId,
          itemType: 'timebox',
          userId,
          title: timeBoxData.title,
          notes: timeBoxData.notes,
          startTime: timeBoxData.startTime,
          endTime: timeBoxData.endTime,
          category: timeBoxData.category,
          status: timeBoxData.status || 'normal',
          taskId: timeBoxData.taskId,
          location: timeBoxData.location,
          createdAt: now,
          updatedAt: now,
        };

        // 1. 立即保存到 IndexedDB
        await createTimeBoxLocal(localTimeBox);
        logInfo('TimeBox 已保存到 IndexedDB', { id: tempId });

        // 2. 處理同步（線上/離線）
        // 即時檢查網路狀態，而非依賴 state
        const currentlyOnline = navigator.onLine;
        logInfo('當前網路狀態', { online: currentlyOnline });

        if (currentlyOnline) {
          try {
            // 準備伺服器資料
            const serverData = {
              userId,
              ...timeBoxData
            };

            // 創建到伺服器
            const serverTimeBox = await createTimeBox(serverData);

            // 替換 IndexedDB 中的臨時記錄
            await replaceTimeBoxTempId(tempId, serverTimeBox.id);

            return serverTimeBox;

          } catch (syncError) {
            logWarn('TimeBox 伺服器同步失敗，加入同步佇列', syncError);

            // 同步失敗，加入佇列待後續處理
            await addToSyncQueue('CREATE_TIMEBOX', tempId, localTimeBox);
            logInfo('已加入同步佇列 (同步失敗)', { id: tempId });

            // 仍返回本地版本
            return localTimeBox;
          }
        } else {
          logInfo('離線模式：直接加入同步佇列');
          // 離線模式：直接加入同步佇列
          await addToSyncQueue('CREATE_TIMEBOX', tempId, localTimeBox);
          logInfo('已加入同步佇列 (離線)', { id: tempId });

          return localTimeBox;
        }

      } catch (err) {
        logError('創建 TimeBox 失敗', err);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [userId]
  );

  return {
    handleCreateTimeBox,
    isSubmitting
  };
};
